<script setup lang="ts">
  import { computed, ref, onMounted, onUnmounted, nextTick, watch } from "vue";
  import CustomTag from "./CustomTag.vue";
  import Accordion from "primevue/accordion";
  import AccordionPanel from "primevue/accordionpanel";
  import AccordionHeader from "primevue/accordionheader";
  import AccordionContent from "primevue/accordioncontent";
  import Button from "primevue/button";
  import { inspectieTypeLabels, productTypeLabels } from "../../../../types";
  import type { Product } from "../../../../types";
  import { matchesFotoContextLabel } from "../utils/fotoContextLabels";
  // @ts-ignore - PhotoSwipe doesn't have perfect TypeScript support
  import PhotoSwipeLightbox from "photoswipe/lightbox";
  import "photoswipe/style.css";

  const props = defineProps<{
    offerte: any;
    currentIndex: number;
    totalCount: number;
  }>();

  const emit = defineEmits<{
    close: [];
    prev: [];
    next: [];
  }>();

  // PhotoSwipe refs
  const galleryRef = ref<HTMLDivElement | null>(null);
  const thumbnailRef = ref<HTMLImageElement | null>(null);
  let lightbox: PhotoSwipeLightbox | null = null;

  const fotoBoven = computed(() => {
    const fotos = props.offerte?.locatie?.fotoBoven_foto;
    if (!fotos || !Array.isArray(fotos)) return null;
    return fotos.find((f: any) => matchesFotoContextLabel(f.context_label, "foto_boven")) || fotos[0] || null;
  });

  function getLatestStatus(offerte: any): string {
    if (!offerte?.status || !Array.isArray(offerte.status) || offerte.status.length === 0) return "nieuw";
    return offerte.status[offerte.status.length - 1]?.status || "nieuw";
  }

  function getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      nieuw: "Nieuw",
      aangepast: "Nieuw",
      verzonden: "Nieuw",
      bekeken: "Nieuw",
      deels_geaccepteerd: "Deels Geaccepteerd",
      alles_geaccepteerd: "Geaccepteerd",
      foute_inzending: "Foute inzending",
      ingepland: "Ingepland",
      ingetrokken: "Ingetrokken",
      afgewezen: "Afgewezen",
      gefactureerd: "Gefactureerd",
    };
    return labels[status] || status;
  }

  function formatCurrency(amount: number | null | undefined): string {
    return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(amount || 0);
  }

  function getConfirmedProducts(offerte: any): any[] {
    if (!offerte?.producten) return [];
    return offerte.producten
      .filter((p: any) => p.prijsType === "aangepast")
      .sort((a: any, b: any) => {
        if (a.productType === "km" && b.productType !== "km") return 1;
        if (a.productType !== "km" && b.productType === "km") return -1;
        return (a.productType || "").localeCompare(b.productType || "");
      });
  }

  function getConfirmedSubtotal(offerte: any): number {
    if (!offerte?.producten) return 0;
    return offerte.producten
      .filter((p: any) => p.prijsType === "aangepast")
      .reduce((total: number, p: any) => total + (p.prijs || 0) * (p.aantal || 1), 0);
  }

  function getProductLabel(productType: string): string {
    return productTypeLabels[productType as keyof typeof productTypeLabels] || productType.toUpperCase();
  }

  function getInspectionTypeLabel(type: string): string {
    return inspectieTypeLabels[type as keyof typeof inspectieTypeLabels] || type.toUpperCase();
  }

  function hasScope(offerte: any, scope: string): boolean {
    if (!offerte?.producten) return false;
    return offerte.producten.some((p: Product) => p.productType === scope);
  }

  // PhotoSwipe
  const initPhotoSwipe = () => {
    if (lightbox) {
      lightbox.destroy();
      lightbox = null;
    }
    const galleryId = "pswp-panel-" + Math.random().toString(36).substring(2, 11);
    if (galleryRef.value) {
      galleryRef.value.id = galleryId;
    }
    lightbox = new PhotoSwipeLightbox({
      gallery: "#" + galleryId,
      children: "a",
      closeOnVerticalDrag: true,
      pinchToClose: true,
      wheelToZoom: true,
      showAnimationDuration: 300,
      hideAnimationDuration: 250,
      thumbSelector: ".panel-hero-thumbnail",
      // @ts-ignore
      pswpModule: () => import("photoswipe"),
    });
    lightbox.addFilter("thumbEl", (_thumbEl: any) => {
      return thumbnailRef.value || _thumbEl;
    });
    lightbox.init();
  };

  const getImageDimensions = async (src: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth || 1920, height: img.naturalHeight || 1080 });
      img.onerror = () => resolve({ width: 1920, height: 1080 });
      img.src = src;
    });
  };

  const updateGalleryDimensions = async () => {
    if (!fotoBoven.value?.remote_url || !galleryRef.value) return;
    try {
      const dimensions = await getImageDimensions(fotoBoven.value.remote_url);
      const link = galleryRef.value.querySelector("a");
      if (link) {
        link.setAttribute("data-pswp-width", dimensions.width.toString());
        link.setAttribute("data-pswp-height", dimensions.height.toString());
      }
    } catch (err) {
      console.error("Failed to get image dimensions:", err);
    }
  };

  const openImageModal = async () => {
    if (!fotoBoven.value?.remote_url) return;
    if (!lightbox) {
      await nextTick();
      await updateGalleryDimensions();
      initPhotoSwipe();
    }
    if (!lightbox) return;
    try {
      lightbox.loadAndOpen(0);
    } catch (error) {
      console.error("Error opening PhotoSwipe:", error);
    }
  };

  watch(
    () => fotoBoven.value?.remote_url,
    async (newUrl) => {
      if (!newUrl) return;
      await nextTick();
      await updateGalleryDimensions();
      initPhotoSwipe();
    },
  );

  // Re-init PhotoSwipe when offerte changes (prev/next)
  watch(
    () => props.offerte?.id,
    async () => {
      if (lightbox) {
        lightbox.destroy();
        lightbox = null;
      }
      await nextTick();
      if (fotoBoven.value?.remote_url) {
        await updateGalleryDimensions();
        initPhotoSwipe();
      }
    },
  );

  onMounted(async () => {
    if (fotoBoven.value?.remote_url) {
      await nextTick();
      await updateGalleryDimensions();
      initPhotoSwipe();
    }
  });

  onUnmounted(() => {
    if (lightbox) {
      lightbox.destroy();
      lightbox = null;
    }
  });
</script>

<template>
  <div class="detail-panel">
    <!-- Panel Header -->
    <div class="panel-header">
      <div class="flex min-w-0 flex-1 items-center gap-2">
        <span class="text-background-900 truncate font-semibold">{{ offerte.offerteNr || "Offerte" }}</span>
        <CustomTag :color="getLatestStatus(offerte)" :label="getStatusLabel(getLatestStatus(offerte))" />
      </div>
      <div class="flex shrink-0 items-center gap-1">
        <span class="text-background-500 mr-1 text-xs">{{ currentIndex + 1 }} van {{ totalCount }}</span>
        <Button
          icon="pi pi-chevron-up"
          size="small"
          severity="secondary"
          text
          rounded
          :disabled="currentIndex === 0"
          @click="emit('prev')"
        />
        <Button
          icon="pi pi-chevron-down"
          size="small"
          severity="secondary"
          text
          rounded
          :disabled="currentIndex === totalCount - 1"
          @click="emit('next')"
        />
        <Button icon="pi pi-times" size="small" severity="secondary" text rounded @click="emit('close')" />
      </div>
    </div>

    <!-- Panel Body (scrollable) -->
    <div class="panel-body">
      <!-- Hero Image -->
      <div v-if="fotoBoven?.remote_url" class="panel-hero group">
        <div ref="galleryRef" class="pswp-gallery">
          <a :href="fotoBoven?.remote_url" :data-pswp-width="1920" :data-pswp-height="1080" target="_blank">
            <img :src="fotoBoven?.remote_url" alt="Locatie foto" />
          </a>
        </div>
        <div class="aspect-video w-full cursor-pointer overflow-hidden rounded-sm" @click="openImageModal">
          <img
            ref="thumbnailRef"
            :src="fotoBoven?.remote_url"
            :alt="`Locatie foto van ${offerte.locatie?.naam || 'de locatie'}`"
            class="panel-hero-thumbnail h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      </div>

      <!-- Location Details -->
      <div v-if="offerte.locatie" class="panel-section">
        <div class="panel-section-header">
          <i class="pi pi-map-marker text-background-500"></i>
          <span class="text-background-800 text-sm font-semibold">Locatie</span>
        </div>
        <div class="panel-detail-grid">
          <div class="panel-detail-item">
            <span class="panel-label">Naam</span>
            <span class="panel-value">{{ offerte.locatie.naam || "-" }}</span>
          </div>
          <div class="panel-detail-item">
            <span class="panel-label">Adres</span>
            <span class="panel-value">
              {{ offerte.locatie.straat }} {{ offerte.locatie.huisnr }}, {{ offerte.locatie.postcode }}
              {{ offerte.locatie.plaats }}
            </span>
          </div>
          <div v-if="offerte.locatie.branche" class="panel-detail-item">
            <span class="panel-label">Branche</span>
            <span class="panel-value">{{ offerte.locatie.branche }}</span>
          </div>
          <div v-if="offerte.locatie.bouwJaar" class="panel-detail-item">
            <span class="panel-label">Bouwjaar</span>
            <span class="panel-value">{{ offerte.locatie.bouwJaar }}</span>
          </div>
          <div v-if="offerte.locatie.gebruiksDoel" class="panel-detail-item">
            <span class="panel-label">Gebruik</span>
            <span class="panel-value">{{ offerte.locatie.gebruiksDoel }}</span>
          </div>
          <div v-if="offerte.locatie.contactNaam" class="panel-detail-item">
            <span class="panel-label">Contact</span>
            <span class="panel-value">{{ offerte.locatie.contactNaam }}</span>
          </div>
          <div v-if="offerte.locatie.contactMail" class="panel-detail-item">
            <span class="panel-label">Email</span>
            <a
              :href="`mailto:${offerte.locatie.contactMail}`"
              class="panel-value text-primary-600 hover:text-primary-800 break-all"
            >
              {{ offerte.locatie.contactMail }}
            </a>
          </div>
          <div v-if="offerte.locatie.telnr" class="panel-detail-item">
            <span class="panel-label">Telefoon</span>
            <a :href="`tel:${offerte.locatie.telnr}`" class="panel-value text-primary-600 hover:text-primary-800">
              {{ offerte.locatie.telnr }}
            </a>
          </div>
        </div>
      </div>

      <!-- Installation Details -->
      <div v-if="offerte.basis" class="panel-section">
        <div class="panel-section-header">
          <i class="pi pi-bolt text-background-500"></i>
          <span class="text-background-800 text-sm font-semibold">Installatie</span>
        </div>
        <div class="panel-detail-grid">
          <div v-if="offerte.basis.aVerdelers" class="panel-detail-item">
            <span class="panel-label">Verdelers</span>
            <span class="panel-value">{{ offerte.basis.aVerdelers }}</span>
          </div>
          <div v-if="offerte.basis.aM2" class="panel-detail-item">
            <span class="panel-label">Oppervlakte</span>
            <span class="panel-value">{{ offerte.basis.aM2 }} m²</span>
          </div>
          <div v-if="offerte.locatie?.installatieJaar" class="panel-detail-item">
            <span class="panel-label">Jaar van aanleg</span>
            <span class="panel-value">{{ offerte.locatie.installatieJaar }}</span>
          </div>
        </div>
        <!-- PV details -->
        <template v-if="hasScope(offerte, 'scope12pi') || hasScope(offerte, 'scope12ebi')">
          <div class="border-background-100 mt-3 border-t pt-3">
            <div class="mb-2 flex items-center gap-2">
              <div class="flex h-5 w-5 items-center justify-center rounded bg-yellow-100">
                <i class="pi pi-bolt text-xs text-yellow-600"></i>
              </div>
              <span class="text-background-800 text-sm font-medium">PV-Installatie</span>
            </div>
            <div class="panel-detail-grid">
              <div v-if="offerte.locatie?.pvInstallatieJaar" class="panel-detail-item">
                <span class="panel-label">PV Jaar</span>
                <span class="panel-value">{{ offerte.locatie.pvInstallatieJaar }}</span>
              </div>
              <div v-if="offerte.basis.pvKwp" class="panel-detail-item">
                <span class="panel-label">Capaciteit</span>
                <span class="panel-value">{{ offerte.basis.pvKwp }} kWp</span>
              </div>
              <div v-if="offerte.basis.pvPanelen" class="panel-detail-item">
                <span class="panel-label">Panelen</span>
                <span class="panel-value">{{ offerte.basis.pvPanelen }}</span>
              </div>
              <div v-if="offerte.basis.pvOmvormers" class="panel-detail-item">
                <span class="panel-label">Omvormers</span>
                <span class="panel-value">{{ offerte.basis.pvOmvormers }}</span>
              </div>
              <div v-if="offerte.basis.pvStrengen" class="panel-detail-item">
                <span class="panel-label">Strengen</span>
                <span class="panel-value">{{ offerte.basis.pvStrengen }}</span>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Products & Pricing -->
      <div v-if="getConfirmedProducts(offerte).length > 0" class="panel-section">
        <div class="panel-section-header">
          <i class="pi pi-euro text-background-500"></i>
          <span class="text-background-800 text-sm font-semibold">Prijsoverzicht</span>
        </div>
        <div class="pricing-list">
          <div
            v-for="product in getConfirmedProducts(offerte)"
            :key="`${product.productType}-${product.prijsType}`"
            class="pricing-row"
          >
            <div class="flex items-center gap-2">
              <span class="text-background-700 text-sm">{{ getProductLabel(product.productType) }}</span>
              <span v-if="product.aantal && product.aantal > 1" class="text-background-500 text-xs">
                ({{ product.aantal }}x)
              </span>
            </div>
            <span class="text-background-900 font-mono text-sm font-medium">
              {{ formatCurrency((product.prijs || 0) * (product.aantal || 1)) }}
            </span>
          </div>
          <div class="pricing-total">
            <span class="text-background-800 text-sm font-medium">Subtotaal</span>
            <span class="text-background-900 font-mono text-sm font-semibold">
              {{ formatCurrency(getConfirmedSubtotal(offerte)) }}
            </span>
          </div>
          <template v-if="offerte.korting && offerte.kortingBedrag">
            <div class="pricing-row">
              <span class="text-sm text-emerald-700">Korting ({{ offerte.kortingPerc || 0 }}%)</span>
              <span class="font-mono text-sm font-medium text-emerald-700">
                - {{ formatCurrency(offerte.kortingBedrag) }}
              </span>
            </div>
            <div class="pricing-total">
              <span class="text-background-900 text-sm font-semibold">Totaal na korting</span>
              <span class="text-primary-700 font-mono text-base font-semibold">
                {{ formatCurrency(offerte.totaalIncKorting) }}
              </span>
            </div>
          </template>
        </div>
      </div>

      <!-- Scope Tags -->
      <div class="flex flex-wrap gap-1.5 px-4 py-2">
        <CustomTag
          v-if="hasScope(offerte, 'scope8pi') || hasScope(offerte, 'scope8ebi')"
          color="scope8pi"
          label="Scope 8"
        />
        <CustomTag v-if="hasScope(offerte, 'scope10')" color="scope10" label="Scope 10" />
        <CustomTag
          v-if="hasScope(offerte, 'scope12pi') || hasScope(offerte, 'scope12ebi')"
          color="scope12pi"
          label="Scope 12"
        />
        <CustomTag v-if="hasScope(offerte, 'nen1010')" color="nen1010" label="NEN 1010" />
        <CustomTag v-if="hasScope(offerte, 'nen3140')" color="nen3140" label="NEN 3140" />
      </div>

      <!-- Voorwaarden & Inspectieplan -->
      <div
        v-if="
          (offerte.voorwaarden && offerte.voorwaarden.length > 0) ||
          (offerte.iplan && Object.keys(offerte.iplan).length > 0)
        "
        class="panel-section"
      >
        <Accordion class="w-full">
          <AccordionPanel v-if="offerte.voorwaarden && offerte.voorwaarden.length > 0" value="voorwaarden">
            <AccordionHeader>
              <div class="flex items-center gap-2">
                <i class="pi pi-file text-sm"></i>
                <span class="text-sm font-medium">Voorwaarden</span>
              </div>
            </AccordionHeader>
            <AccordionContent>
              <div class="space-y-3 pt-2">
                <div
                  v-for="(voorwaarde, index) in offerte.voorwaarden"
                  :key="index"
                  class="bg-background-50 border-background-200 rounded-sm border p-3"
                >
                  <h4 class="text-background-900 mb-2 flex items-start text-sm font-semibold">
                    <span
                      class="mt-0.5 mr-2 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-orange-100 text-xs font-bold text-orange-600"
                    >
                      {{ (index as number) + 1 }}
                    </span>
                    {{ voorwaarde.omschrijving }}
                  </h4>
                  <ul class="ml-7 space-y-1">
                    <li
                      v-for="waarde in voorwaarde.waarde"
                      :key="waarde"
                      class="text-background-700 flex items-start text-xs leading-relaxed"
                    >
                      <span class="mt-1.5 mr-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-orange-400"></span>
                      {{ waarde }}
                    </li>
                  </ul>
                </div>
              </div>
            </AccordionContent>
          </AccordionPanel>

          <AccordionPanel v-if="offerte.iplan && Object.keys(offerte.iplan).length > 0" value="inspectieplan">
            <AccordionHeader>
              <div class="flex items-center gap-2">
                <i class="pi pi-list-check text-sm"></i>
                <span class="text-sm font-medium">Inspectieplan</span>
              </div>
            </AccordionHeader>
            <AccordionContent>
              <div class="space-y-3 pt-2">
                <div v-if="offerte.iplan.algemeen" class="bg-background-50 border-background-200 rounded-sm border p-3">
                  <h4 class="text-background-900 mb-2 text-sm font-semibold">
                    {{ offerte.iplan.algemeen.title || "Algemeen" }}
                  </h4>
                  <div v-if="offerte.iplan.algemeen.sections" class="space-y-2">
                    <div v-for="(section, si) in offerte.iplan.algemeen.sections" :key="si" class="ml-4">
                      <h5 v-if="section.heading" class="text-background-800 mb-1 text-sm font-medium">
                        {{ section.heading }}
                      </h5>
                      <p v-if="section.content" class="text-background-700 mb-1 text-xs">
                        {{ section.content }}
                      </p>
                      <ul v-if="section.items?.length" class="space-y-0.5">
                        <li
                          v-for="item in section.items"
                          :key="item"
                          class="text-background-700 flex items-start text-xs"
                        >
                          <span class="mt-1.5 mr-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-blue-400"></span>
                          {{ item }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div
                  v-if="offerte.iplan.inspections"
                  v-for="(inspectionContent, inspectionType) in offerte.iplan.inspections"
                  :key="inspectionType"
                  class="bg-background-50 border-background-200 rounded-sm border p-3"
                >
                  <h4 class="text-background-900 mb-2 text-sm font-semibold">
                    {{ inspectionContent.title || getInspectionTypeLabel(String(inspectionType)) }}
                  </h4>
                  <div v-if="inspectionContent.sections" class="space-y-2">
                    <div v-for="(section, si) in inspectionContent.sections" :key="si" class="ml-4">
                      <h5 v-if="section.heading" class="text-background-800 mb-1 text-sm font-medium">
                        {{ section.heading }}
                      </h5>
                      <p v-if="section.content" class="text-background-700 mb-1 text-xs">
                        {{ section.content }}
                      </p>
                      <ul v-if="section.items?.length" class="space-y-0.5">
                        <li
                          v-for="item in section.items"
                          :key="item"
                          class="text-background-700 flex items-start text-xs"
                        >
                          <span class="mt-1.5 mr-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-green-400"></span>
                          {{ item }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionPanel>
        </Accordion>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .detail-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--p-background-0);
    border: 1px solid var(--p-background-200);
    border-radius: 0.25rem;
    overflow: hidden;
  }

  .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--p-background-200);
    background: var(--p-background-50);
    flex-shrink: 0;
  }

  .panel-body {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .panel-hero {
    position: relative;
    padding: 0.75rem;
  }

  /* Hide the PhotoSwipe gallery but keep it accessible */
  .pswp-gallery {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    margin: -1px;
    padding: 0;
    border: 0;
  }

  .panel-section {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--p-background-100);
  }

  .panel-section-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .panel-detail-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }

  .panel-detail-item {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  .panel-label {
    font-size: 0.7rem;
    color: var(--p-background-500);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .panel-value {
    font-size: 0.85rem;
    color: var(--p-background-800);
  }

  .pricing-list {
    display: flex;
    flex-direction: column;
  }

  .pricing-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.4rem 0;
  }

  .pricing-total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    border-top: 1px solid var(--p-background-200);
    margin-top: 0.25rem;
  }
</style>
